/**
 * DesignDayItem Component
 *
 * Single design day row with checkbox, info, and view button.
 */

import type { DesignDayMetadata, ParsedDesignDay } from '../../types';

export interface DesignDayItemProps {
  designDay: DesignDayMetadata | ParsedDesignDay;
  isSelected: boolean;
  isRecommended: boolean;
  onToggle: () => void;
  onViewDetails: () => void;
}

export function DesignDayItem({
  designDay,
  isSelected,
  isRecommended,
  onToggle,
  onViewDetails,
}: DesignDayItemProps) {
  // Format month/day
  const dateStr = `${getMonthName(designDay.month)} ${designDay.dayOfMonth}`;

  return (
    <div
      className={`flex items-center gap-3 rounded-md border p-3 transition-colors ${
        isSelected
          ? 'border-primary bg-primary/5'
          : 'border-border hover:border-primary/50 hover:bg-muted/50'
      }`}
    >
      {/* Checkbox */}
      <label className="flex cursor-pointer items-center">
        <input
          type="checkbox"
          checked={isSelected}
          onChange={onToggle}
          className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
        />
      </label>

      {/* Design day info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-medium truncate" title={designDay.name}>
            {designDay.name}
          </span>
          {isRecommended && (
            <span className="shrink-0 rounded bg-green-100 px-1.5 py-0.5 text-xs font-medium text-green-700 dark:bg-green-900/30 dark:text-green-400">
              Recommended
            </span>
          )}
        </div>
        <div className="flex items-center gap-3 text-sm text-muted-foreground mt-0.5">
          <span>{dateStr}</span>
          <span className="text-primary font-medium">
            {formatTemperature(designDay.dryBulbTemp)}
          </span>
          {designDay.wetBulbTemp !== undefined && (
            <span className="text-muted-foreground">
              WB: {formatTemperature(designDay.wetBulbTemp)}
            </span>
          )}
        </div>
      </div>

      {/* Info tooltip */}
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          // TODO: Show tooltip with design day explanation
        }}
        className="shrink-0 rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
        title={getDesignDayDescription(designDay)}
        aria-label="Info"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <circle cx="12" cy="12" r="10" />
          <path d="M12 16v-4" />
          <path d="M12 8h.01" />
        </svg>
      </button>

      {/* View button */}
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onViewDetails();
        }}
        className="shrink-0 rounded border px-2 py-1 text-sm hover:bg-muted"
      >
        View
      </button>
    </div>
  );
}

/**
 * Get month name abbreviation.
 */
function getMonthName(month: number): string {
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return months[month - 1] ?? 'Unknown';
}

/**
 * Format temperature with unit.
 */
function formatTemperature(temp: number): string {
  return `${temp.toFixed(1)}°C`;
}

/**
 * Get description for design day type.
 */
function getDesignDayDescription(designDay: DesignDayMetadata): string {
  const name = designDay.name.toLowerCase();

  if (name.includes('htg') || name.includes('heating')) {
    if (name.includes('99.6')) {
      return 'Heating design condition at 99.6% annual frequency. Use for conservative heating equipment sizing.';
    }
    if (name.includes('99%')) {
      return 'Heating design condition at 99% annual frequency. Use for typical heating equipment sizing.';
    }
    return 'Heating design condition for winter equipment sizing.';
  }

  if (name.includes('clg') || name.includes('cooling')) {
    if (name.includes('.4%') || name.includes('0.4%')) {
      return 'Cooling design condition at 0.4% annual frequency. Use for conservative cooling equipment sizing.';
    }
    if (name.includes('1%')) {
      return 'Cooling design condition at 1% annual frequency. Use for typical cooling equipment sizing.';
    }
    if (name.includes('2%')) {
      return 'Cooling design condition at 2% annual frequency. Use for economical cooling equipment sizing.';
    }
    return 'Cooling design condition for summer equipment sizing.';
  }

  if (name.includes('extreme')) {
    if (name.includes('min')) {
      return 'Extreme minimum temperature condition for special design considerations.';
    }
    if (name.includes('max')) {
      return 'Extreme maximum temperature condition for special design considerations.';
    }
    return 'Extreme weather condition for special design considerations.';
  }

  return `Design day condition: ${designDay.dayType}`;
}
